import { Box, Button, Typography } from "@mui/material";
import type { Selection, TimelineData } from "../models";
import { dateTimeText, durationText, numberText } from "../timeline";

interface Props {
	data: TimelineData;
	selection: Selection | null;
	clear: () => void;
}
function EmptyDetails() {
	return (
		<Box className="timeline-details timeline-details-empty">
			<Typography variant="body2" color="text.secondary">
				Select an event to see its details.
			</Typography>
		</Box>
	);
}

export function TimelineDetails({ data, selection, clear }: Props) {
	if (!selection) return <EmptyDetails />;
	let title = "";
	let context = "";
	let comment = "";
	let eventKind: Selection["kind"] | "cobble" = selection.kind;
	let fields: [string, string][] = [];
	if (selection.kind === "billet") {
		const b = data.billets.find((b) => b.Id === selection.id);
		if (!b) return <EmptyDetails />;
		eventKind = b.IsGood === false ? "cobble" : "billet";
		title = `Billet ${b.Id}${b.IsGood === false ? " · Cobble flagged" : ""}`;
		context = `WO ${b.WorkOrder ?? "—"} · Campaign ${b.CampaignId ?? "—"} · ${b.SectionCode ?? "—"} · ${b.Product ?? "—"}`;
		fields = [
			["RHF discharge", dateTimeText(b.start)],
			[b.IsEstimated ? "Estimated end" : "Recorded end", dateTimeText(b.end)],
			["Duration", durationText(b.start, b.end)],
			["Entry temp avg", numberText(b.StartTemperatureAvg)],
			["Exit temp avg", numberText(b.EndTemperatureAvg)],
			["Heat", b.Heat ?? "—"],
			["Grade", b.Grade ?? "—"],
			["Weight", `${numberText(b.BilletWeight)} lb`],
			["Length", `${numberText(b.Length)} in`],
			[
				"Billet size",
				`${numberText(b.BilletHeight)} × ${numberText(b.BilletWidth)} in`,
			],
			["Shift / crew", `${b.ShiftId ?? "—"} / ${b.CrewId ?? "—"}`],
			["Tracking", String(b.TrackingId ?? "—")],
		];
	} else if (selection.kind === "stop") {
		const stop = data.stops.find((e) => e.Id === selection.id);
		if (!stop) return <EmptyDetails />;
		title =
			stop.DelayCodePath?.split("/").join(" > ") ||
			stop.Cause ||
			"Not declared";
		context = [
			stop.IsMicroDelay && "Microdelay",
			stop.IsDelayManual && "Manually recorded",
		]
			.filter(Boolean)
			.join(" · ");
		fields = [
			["Start", dateTimeText(stop.start)],
			["End", stop.end === null ? "Open" : dateTimeText(stop.end)],
			["Duration", durationText(stop.start, stop.end)],
			["Category", stop.Category ?? "—"],
			["Campaign", String(stop.CampaignId ?? "—")],
		];
		comment = stop.Comments ?? "";
	} else if (selection.kind === "roll") {
		const r = data.rolls.find((e) => e.Id === selection.id);
		if (!r) return <EmptyDetails />;
		title = `${r.Stand} · Roll change`;
		fields = [
			["Time", dateTimeText(r.start)],
			["Installed rollset", r.InstalledRollset ?? "Removed"],
			["Previous rollset", r.PreviousRollset ?? "—"],
			["Campaign", String(r.CampaignId ?? "—")],
		];
	} else if (selection.kind === "campaign") {
		const c = data.campaigns.find((e) => e.Id === selection.id);
		if (!c) return <EmptyDetails />;
		title = `Campaign ${c.Id}`;
		context = `${c.SectionCode ?? "—"} · ${c.Product ?? "—"}`;
		fields = [
			["Recorded start", dateTimeText(c.start)],
			[
				"Recorded end",
				c.end === null
					? c.nextStart
						? "Missing"
						: "Open"
					: dateTimeText(c.end),
			],
		];
	} else {
		const wo = data.workOrders.find((e) => e.Id === selection.id);
		if (!wo) return <EmptyDetails />;
		title = `WO ${wo.WorkOrder ?? "Unassigned"}`;
		context = `${wo.SectionCode ?? "—"} · ${wo.Product ?? "—"}`;
		fields = [
			["First recorded billet", String(wo.FirstBilletId)],
			["Start", dateTimeText(wo.start)],
			["Next WO boundary", dateTimeText(wo.end)],
		];
	}
	return (
		<Box className={`timeline-details timeline-details-${eventKind}`}>
			<Box className="timeline-details-header">
				<Box
					component="span"
					className="timeline-details-dot"
					aria-hidden="true"
				/>
				<Typography
					variant="subtitle2"
					className="timeline-details-title"
					title={title}
				>
					{title}
				</Typography>
				{context && (
					<Typography
						variant="caption"
						color="text.secondary"
						className="timeline-details-context"
						title={context}
					>
						{context}
					</Typography>
				)}
				<Button size="small" onClick={clear} sx={{ ml: "auto", flexShrink: 0 }}>
					Clear selection
				</Button>
			</Box>
			{comment && (
				<Typography
					variant="caption"
					className="timeline-details-comment"
					title={comment}
				>
					{comment}
				</Typography>
			)}
			<Box className="timeline-details-body">
				<Box component="dl" className="timeline-detail-fields">
					{fields.map(([label, value]) => (
						<div key={label}>
							<dt>{label}</dt>
							<dd>{value}</dd>
						</div>
					))}
				</Box>
			</Box>
		</Box>
	);
}
